import { useMemo } from "react";
import { generateThemeColors } from "@tellescope/utils";
import type { Theming } from "../schema";

/**
 * CSS variable names that can be themed.
 * These correspond to the CSS custom properties defined in globals.css.
 */
const CSS_VARIABLES = {
  primary: "--primary",
  primaryForeground: "--primary-foreground",
  secondary: "--secondary",
  secondaryForeground: "--secondary-foreground",
} as const;

/**
 * Hook that generates inline styles for scoped CSS variable theming.
 *
 * This hook is used to apply theme colors to a specific container element
 * without affecting other parts of the UI. The CSS variables are set via
 * inline styles on a wrapper element, which cascade to all children that
 * use `var(--primary)`, etc.
 *
 * @param theming - The theming configuration from the portal schema
 * @returns A React.CSSProperties object with CSS custom properties
 *
 * @example
 * ```tsx
 * const themeStyles = useThemeStyles(schema.settings?.theming);
 * return (
 *   <div style={themeStyles}>
 *     {/* Content will use themed CSS variables *\/}
 *   </div>
 * );
 * ```
 */
export function useThemeStyles(theming: Theming | undefined): React.CSSProperties {
  return useMemo(() => {
    const styles: Record<string, string> = {};

    // Apply primary color if set
    if (theming?.primaryColor) {
      const colors = generateThemeColors(theming.primaryColor);
      if (colors) {
        styles[CSS_VARIABLES.primary] = colors.cssValue;
        styles[CSS_VARIABLES.primaryForeground] = colors.foregroundCssValue;
      }
    }

    // Apply secondary color if set
    if (theming?.secondaryColor) {
      const colors = generateThemeColors(theming.secondaryColor);
      if (colors) {
        styles[CSS_VARIABLES.secondary] = colors.cssValue;
        styles[CSS_VARIABLES.secondaryForeground] = colors.foregroundCssValue;
      }
    }

    // Apply font family if set (and not system default)
    if (theming?.fontFamily && theming.fontFamily !== "system") {
      styles.fontFamily = `"${theming.fontFamily}", ui-sans-serif, system-ui, sans-serif`;
    }

    return styles as React.CSSProperties;
  }, [theming?.primaryColor, theming?.secondaryColor, theming?.fontFamily]);
}
